import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { nextSku, cleanText } from '../lib/pi.js';

// หมวดหมู่ = ต้นไม้หมวดตัวจริงของ PD (pd.categories) ดึงผ่าน /api/pd/meta
// เรียงเป็น parent → ลูก แล้วเยื้องบรรทัด ให้เลือกง่ายเหมือนหน้า PD
function flattenCats(cats) {
  const byParent = new Map();
  (cats || []).forEach(c => {
    const k = c.parent_id ?? 'root';
    if (!byParent.has(k)) byParent.set(k, []);
    byParent.get(k).push(c);
  });
  byParent.forEach(list => list.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || String(a.name).localeCompare(String(b.name), 'th')));
  const out = [];
  const walk = (key, depth) => (byParent.get(key) || []).forEach(c => {
    out.push({ id: c.id, name: c.name, depth, label: `${'  '.repeat(depth)}${depth ? '└ ' : ''}${c.name}` });
    walk(c.id, depth + 1);
  });
  walk('root', 0);
  return out;
}

// Create-product drawer. Two modes:
//  - PI mode:   line + supplier passed → prefill from PI, save also writes the
//               supplier_products match-key mapping, returns the new product.
//  - standalone: no line → ahead-of-time creation (เมนู "สร้างสินค้าใหม่").
// ราคาซื้อใส่ตรงนี้ได้ (ไม่บังคับ) → ไหลไป pd.products.purchase_cost ทันที
// ถ้าไม่ใส่ ต้นทุนจะอัปเดตเองตอนกดรับของเข้า/ส่งใบ PO ตามเดิม; บาร์โค้ดกับรูปไม่บังคับ
export default function ProductDrawer({ line, supplier, currency, initialBarcode, initialPrice, initialPhotoUrl, onClose, onSaved }) {
  const piMode = !!line;
  const [brands, setBrands] = useState([]);
  const [name, setName] = useState(line?.desc ?? '');
  const [brandName, setBrandName] = useState(supplier?.default_brand?.name ?? '');
  // initial* = ค่าที่ยิงมาจากมือถือ (หน้า "ยิงบาร์โค้ด") — ที่เรียกอื่นไม่ส่งมา ค่าเดิมเลยไม่เปลี่ยน
  const [price, setPrice] = useState(initialPrice ?? '');
  const [cost, setCost] = useState('');   // ราคาซื้อ/ต้นทุนต่อชิ้น — ไหลไป pd.products.purchase_cost
  const [barcode, setBarcode] = useState(initialBarcode ?? '');
  // Camp Studio convention: our SKU = the supplier's item code (verified in the
  // live catalog, e.g. Snow Peak TP-672 → SKU TP-672). PI mode seeds SKU from
  // the PI line; standalone mode falls back to a generated running number.
  const [sku, setSku] = useState(line?.code ?? '');
  const [category, setCategory] = useState('');     // id ของหมวดใน PD (bigint) เก็บเป็น string ใน select
  const [pdCats, setPdCats] = useState([]);
  const [photo, setPhoto] = useState(null);      // File
  const [photoUrl, setPhotoUrl] = useState(initialPhotoUrl ?? null); // object URL preview (หรือรูปป้ายที่ถ่ายมาจากมือถือ)
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.from('brands').select('id, name').order('name').then(({ data }) => setBrands(data ?? []));
    // ดึงหมวดหมู่ตัวจริงจาก PD — ถ้าดึงไม่ได้ก็ยังสร้างสินค้าได้ (แค่ไม่มีหมวดให้เลือก)
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const r = await fetch('/api/pd/meta', { headers: { Authorization: `Bearer ${session.access_token}` } });
        const o = await r.json().catch(() => ({}));
        if (r.ok) setPdCats(flattenCats(o.categories));
      } catch { /* PD ล่มชั่วคราว */ }
    })();
  }, []);
  useEffect(() => {
    if (piMode) return; // SKU already seeded from the supplier code on the PI
    let on = true;
    nextSku(brandName).then(s => { if (on) setSku(s); });
    return () => { on = false; };
  }, [brandName, piMode]);

  function pickPhoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { setErr('รูปใหญ่เกิน 2 MB'); return; }
    setPhoto(f);
    setPhotoUrl(URL.createObjectURL(f));
  }
  function removePhoto(e) {
    e.stopPropagation();
    setPhoto(null);
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl(null);
  }

  async function save() {
    if (busy) return;
    if (!name.trim()) { setErr('กรอกชื่อสินค้าก่อน'); return; }
    // ราคาขายไม่บังคับแล้ว (16 ก.ค. 2026): เว้นว่าง = สร้างเป็น draft ใน PD ให้ Purim ตามใส่ราคาทีหลัง
    const priceNum = parseFloat(String(price).replace(/,/g, ''));
    const hasPrice = Number.isFinite(priceNum) && priceNum > 0;
    if (String(price).trim() && !hasPrice) { setErr('ราคาขายไม่ถูกต้อง — ใส่ตัวเลข หรือเว้นว่างไว้ไปใส่ใน PD ทีหลัง'); return; }
    const costNum = parseFloat(String(cost).replace(/,/g, ''));
    const hasCost = Number.isFinite(costNum) && costNum > 0;
    if (String(cost).trim() && !hasCost) { setErr('ราคาซื้อไม่ถูกต้อง — ใส่ตัวเลข หรือเว้นว่างไว้'); return; }
    setBusy(true); setErr('');
    try {
      // brand: resolve by name, create if new
      let brandId = null;
      if (brandName.trim()) {
        const hit = brands.find(b => b.name.toLowerCase() === brandName.trim().toLowerCase());
        if (hit) brandId = hit.id;
        else {
          const { data, error } = await supabase.from('brands').insert({ name: brandName.trim() }).select('id').single();
          if (error) throw new Error(`สร้างแบรนด์ไม่สำเร็จ: ${error.message}`);
          brandId = data.id;
        }
      }
      // optional photo → sourcepoint-media bucket (creation must succeed without it)
      // รูปป้ายที่ถ่ายมาจากมือถืออยู่ใน bucket แล้ว — ใช้ URL เดิมได้เลย (กด ✕ ในกรอบรูปเพื่อไม่ใช้)
      let imageUrl = (!photo && photoUrl && /^https?:/.test(photoUrl)) ? photoUrl : null;
      if (photo) {
        const ext = (photo.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `products/${(sku || 'p').replace(/[^A-Za-z0-9_-]/g, '')}-${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from('sourcepoint-media').upload(path, photo, { upsert: false });
        if (error) throw new Error(`อัปโหลดรูปไม่สำเร็จ: ${error.message}`);
        imageUrl = supabase.storage.from('sourcepoint-media').getPublicUrl(path).data.publicUrl;
      }
      const payload = {
        name: name.trim(),
        brand_id: brandId,
        sku: cleanText(sku).trim() || null,
        barcode: cleanText(barcode).trim() || null,
        default_price: hasPrice ? priceNum : null,
        image_url: imageUrl,
      };
      // เก็บชื่อหมวดไว้ในตารางฝั่ง BUY เพื่ออ้างอิง — ตัวที่มีผลจริงคือ category_id ที่ส่งไป PD ด้านล่าง
      const catName = pdCats.find(c => String(c.id) === String(category))?.name || '';
      if (catName) payload.category = catName;
      const { data: product, error: pErr } = await supabase.from('products')
        .insert(payload).select('id, name, sku, image_url').single();
      if (pErr) throw pErr;

      // PI mode → remember the supplier's code for auto-match next time
      if (piMode && supplier?.id) {
        const { error } = await supabase.from('supplier_products').upsert({
          supplier_id: supplier.id,
          supplier_product_code: line.code,
          product_id: product.id,
          last_unit_cost_foreign: line.unit ?? null,
          last_currency: currency ?? supplier.currency ?? null,
          last_seen_at: new Date().toISOString(),
        }, { onConflict: 'supplier_id,supplier_product_code' });
        if (error) throw error;
      }

      // สร้างเข้า PD (product master) ด้วยทันที — ไม่มีราคา = draft; ถ้าพลาดไม่บล็อกงานซื้อ
      // (มี cron absorb รายชั่วโมงเก็บตกให้อยู่แล้ว; ต้นทุนจริงไหลเข้า PD ตอนส่งใบ PO)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && payload.sku) {
          await fetch('/api/pd/create-product', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({
              sku: payload.sku,
              name: payload.name,
              brand_id: brandId,
              brand_name: brandName.trim() || undefined,
              category_id: category || undefined,   // = pd.categories.id (หมวดที่ PD โชว์จริง)
              purchase_cost: hasCost ? costNum : undefined,
              barcode: payload.barcode || undefined,
              image_url: imageUrl || undefined,
              default_price: hasPrice ? priceNum : undefined,
              source_ref: product.id,
            }),
          });
        }
      } catch { /* PD ล่มชั่วคราว → absorb cron ตามเก็บ */ }
      onSaved({ ...product, purchase_cost: hasCost ? costNum : null });
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim open" onClick={onClose} />
      <aside className="drawer open" aria-hidden="false">
        <div className="drawer-head">
          <div>
            <div className="eyebrow">สินค้าใหม่ในระบบ · New product</div>
            <h2>สร้างสินค้า</h2>
          </div>
          <button className="xbtn" onClick={onClose} aria-label="ปิด">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
          </button>
        </div>

        <div className="drawer-body">
          {piMode && !!line.code && (
            <div className="from-pi">
              <div><div className="k">รหัสซัพพลายเออร์ · จำไว้ใช้ครั้งหน้า</div><div className="v mono">{line.code}</div></div>
              <div style={{ textAlign: 'right' }}><div className="k">จาก PI</div><div className="v mono">{line.qty} ชิ้น</div></div>
            </div>
          )}

          <label className={`photo-up ${photoUrl ? 'has' : ''}`}>
            <input type="file" accept="image/*" hidden onChange={pickPhoto} />
            {photoUrl ? (
              <>
                <img src={photoUrl} alt="" />
                <button type="button" className="pu-remove" onClick={removePhoto} aria-label="ลบรูป">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
                </button>
              </>
            ) : (
              <div>
                <div className="pu-ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="4" y="5" width="16" height="14" rx="1.5" stroke="#0A0A0A" strokeWidth="1.4"/><circle cx="9" cy="10" r="1.5" stroke="#0A0A0A" strokeWidth="1.4"/><path d="M5 17l4-4 3 3 3-3 4 4" stroke="#0A0A0A" strokeWidth="1.4"/></svg></div>
                <h4>อัปโหลดรูปสินค้า</h4>
                <p>ไม่บังคับ · สูงสุด 2 MB — ไม่ใส่ก็ทำใบซื้อต่อได้</p>
              </div>
            )}
          </label>

          <div className="field">
            <div className="lbl"><span className="th">ชื่อสินค้า</span><span className="en">Product name</span></div>
            <input className="under" value={name} onChange={e => setName(e.target.value)} placeholder="ชื่อสินค้า" />
          </div>
          <div className="field">
            <div className="lbl"><span className="th">แบรนด์</span><span className="en">Brand</span>{piMode && <span className="opt">ค่าเริ่มต้นจากซัพพลายเออร์</span>}</div>
            <input className="under" value={brandName} onChange={e => setBrandName(e.target.value)} list="pd-brands" />
            <datalist id="pd-brands">{brands.map(b => <option key={b.id} value={b.name} />)}</datalist>
          </div>

          <div className="fx-row">
            <div className="field price-thb">
              <div className="lbl"><span className="th">ราคาขายในไทย</span><span className="opt">เว้นว่าง = draft</span></div>
              <span className="cur">฿</span>
              <input className="under mono" inputMode="numeric" value={price} onChange={e => setPrice(e.target.value)} placeholder="เว้นว่าง = draft ใน PD" />
            </div>
            <div className="field price-thb">
              {/* กฎ Purim 1 ส.ค.: ต้นทุนใน PD = บาทเท่านั้น — เงินนอกต้องแปลงด้วยเรทตามเอกสารก่อนใส่ */}
              <div className="lbl" title="ต้นทุนใน PD เก็บเป็นบาทเท่านั้น — ถ้าซื้อเงินต่างประเทศ แปลงด้วยเรทตามเอกสารก่อนใส่">
                <span className="th">ราคาซื้อ (ต้นทุน/ชิ้น)</span><span className="opt">บาทเท่านั้น — เงินนอกแปลงเรทก่อน</span></div>
              <span className="cur">฿</span>
              <input className="under mono" inputMode="decimal" value={cost} onChange={e => setCost(e.target.value)} placeholder="ใส่ตอนนี้ก็ได้" />
            </div>
          </div>
          <div className="cost-note">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flex: 'none', marginTop: 1 }}><circle cx="12" cy="12" r="9" stroke="#BF7A15" strokeWidth="1.4"/><path d="M12 11v5M12 8h.01" stroke="#BF7A15" strokeWidth="1.6"/></svg>
            <span>สินค้าใหม่ถูกสร้างเข้า PD ให้อัตโนมัติ — ไม่ใส่ <b>ราคาขาย</b> = draft (ยังไม่ขึ้น POS/Zort) · <b>ราคาซื้อ</b> ใส่ตรงนี้ก็ได้ หรือปล่อยว่างแล้วมันจะอัปเดตเองตอนกดรับของเข้า · <b>แบรนด์</b> กับ <b>หมวดหมู่</b> เป็นคนละช่องใน PD ใส่ได้ทั้งคู่</span>
          </div>

          <div className="fx-row">
            <div className="field">
              <div className="lbl"><span className="th">บาร์โค้ด</span><span className="opt">ไม่บังคับ</span></div>
              <input className="under mono" value={barcode} onChange={e => setBarcode(e.target.value)} placeholder="เว้นว่างได้" />
            </div>
            <div className="field">
              <div className="lbl"><span className="th">SKU</span><span className="opt">{piMode ? 'ตั้งจากรหัสในใบ PI · แก้ได้' : 'อัตโนมัติ · แก้ได้'}</span></div>
              <input className="under mono" value={sku} onChange={e => setSku(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <div className="lbl"><span className="th">หมวดหมู่</span><span className="en">Category</span>
              <span className="opt">{pdCats.length ? 'หมวดเดียวกับใน PD' : 'กำลังโหลดจาก PD…'}</span></div>
            <select className="under" value={category} onChange={e => setCategory(e.target.value)} disabled={!pdCats.length}>
              <option value="">— ไม่ระบุ —</option>
              {pdCats.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          <p className="err-note">{err}</p>
        </div>

        <div className="drawer-foot">
          <span className="txt">{piMode ? 'สร้างและเชื่อมเข้า PI' : 'สร้างสินค้า'}</span>
          <button className="arrow" onClick={save} disabled={busy} aria-label="สร้าง">
            <svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
          </button>
        </div>
      </aside>
    </>
  );
}
